import { Grid } from "@mui/material";
import { setInnCode } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import BookmarkPlan from "components/common/Bookmark";
import Button from "components/common/Button";
import HTMLRenderer from "components/common/HTMLRenderer";
import { setFeedbackDetails } from "components/common/Snackbar/snackbar.slice";
import Typography from "components/common/Typography";
import PlanAssignment from "components/features/PlanAssignment";
import { useTranslate } from "hooks/useTranslate";
import { useContext, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import {
  abovePropertyViewTypePlan,
  abovePropertyViewTypeTemplate,
  accessTypes,
  tabConstants
} from "utils/CommonConstants";
import { isAboveUserPlanBookmarked } from "utils/CommonFunctions";
import { getPlanDetailsRoute } from "utils/StoreUtils";
import { setisBookmarked } from "../../../planDetails/PlanDetail.slice";

export default function PlanDetails() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const translate = useTranslate();
  const { isMobileScreen } = useContext(ThemeContext);
  const [searchParams] = useSearchParams();

  const redirectFrom = searchParams.get("redirectFrom");
  const isTemplatePlan = redirectFrom === tabConstants.AssignedCustomPlans;

  const myPlanDetails: any = useAppSelector((state) => state.myPlanDetails.myPlanDetails);
  const planProgressSummary: any = useAppSelector((state) => state.aboveProperty.planProgressSummary);

  const [isOpenPlanAssignmentDialog, setIsOpenPlanAssignmentDialog] = useState(false);
  const planDetails = isTemplatePlan ? myPlanDetails : planProgressSummary;

  const getTimeData = () => {
    return (
      <Grid
        item
        xs={12}
        sm={6}
        display="flex"
        gap={"8px"}
        alignSelf="right"
        justifyContent={{ xs: "space-between", sm: "flex-end" }}
        alignItems={"center"}
        marginTop={{ xs: "16px", sm: 0 }}
      >
        <Grid item display={"flex"} flexDirection={"row"} gap={"8px"} alignItems={"center"}>
          <BookmarkPlan
            aboveUserPayload={{
              isAboveUsertype: true,
              viewType: isTemplatePlan ? abovePropertyViewTypeTemplate : abovePropertyViewTypePlan
            }}
            cmsId={planDetails?.id}
            isBookmarked={isAboveUserPlanBookmarked(
              planDetails?.id,
              isTemplatePlan ? "bookmarkedTemplate" : "bookmarkedIdList"
            )}
          />
        </Grid>
      </Grid>
    );
  };

  const handleCloseDialog = () => {
    setIsOpenPlanAssignmentDialog(false);
  };

  // A library plan with no applicable properties is no longer available in the Library.
  const isLibraryPlanUnavailable = !isTemplatePlan && !planDetails?.properties?.length;

  const showPlanUnavailableMessage = () => {
    dispatch(
      setFeedbackDetails({
        shouldShow: true,
        setState: setFeedbackDetails({ shouldShow: false }),
        message: translate("common.planNoLongerInLibrary")
      })
    );
  };

  return (
    <>
      <Grid container sx={{ backgroundColor: tokens.Background.General.Primary }} padding="20px">
        {isMobileScreen && getTimeData()}{" "}
        <Grid item xs={12} display="flex" sx={{ flexDirection: { xs: "column", sm: "row" } }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="h4" showBold color={tokens.Text.General.PrimaryHeadline}>
              {planDetails?.name}
            </Typography>
          </Grid>

          {!isMobileScreen && getTimeData()}
        </Grid>
        <Grid item xs={12} marginTop={"10px"} sx={{ overflowWrap: "break-word" }}>
          <Typography component="div" variant={"body2"} color={tokens.Text.General.PrimaryBody}>
            <HTMLRenderer content={planDetails?.summary} />
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          marginTop={"10px"}
          display="flex"
          flexDirection={isMobileScreen ? "column" : "row"}
          justifyContent={"flex-end"}
          gap={1}
        >
          <Button
            sx={{ width: isMobileScreen ? "100%" : "" }}
            variant="secondary"
            label={translate("common.viewPlanDetails")}
            onClick={() => {
              if (isLibraryPlanUnavailable) {
                showPlanUnavailableMessage();
                return;
              }
              const route = getPlanDetailsRoute();
              dispatch(setisBookmarked(planDetails?.isBookmarked));
              if (isTemplatePlan) {
                navigate(`/${route}/${planDetails?.id}`);
              } else {
                dispatch(setInnCode(planDetails.properties[0]));
                navigate(`/plandetails/${planDetails?.id}`);
              }
            }}
          />

          <Button
            sx={{ width: isMobileScreen ? "100%" : "" }}
            variant="primary"
            label="Assign Plan"
            accessType={accessTypes.assign}
            onClick={() => {
              if (isLibraryPlanUnavailable) {
                showPlanUnavailableMessage();
                return;
              }
              if (myPlanDetails?.totalCards === 0 && isTemplatePlan)
                dispatch(
                  setFeedbackDetails({
                    shouldShow: true,
                    setState: setFeedbackDetails({ shouldShow: false }),
                    message: "The plan cannot be assigned as there are no action cards for the plan"
                  })
                );
              else setIsOpenPlanAssignmentDialog(true);
            }}
          />
        </Grid>
      </Grid>
      {isOpenPlanAssignmentDialog && (
        <PlanAssignment
          flow="third"
          handleCloseDialog={handleCloseDialog}
          plans={[planDetails]}
          userSelectionFlow="third"
          isTemplatePlan={isTemplatePlan}
          open={isOpenPlanAssignmentDialog}
          isPlanProgress
          inncodes={planDetails.properties}
        />
      )}
    </>
  );
}
